import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'
import type { User, UserRole, SubscriptionTier } from '../types/database.types'

interface AuthState {
  // Auth state
  user: SupabaseUser | null
  session: Session | null
  profile: User | null
  isLoading: boolean
  isInitialized: boolean
  _hasHydrated: boolean

  // Actions
  setUser: (user: SupabaseUser | null) => void
  setSession: (session: Session | null) => void
  setProfile: (profile: User | null) => void
  setLoading: (loading: boolean) => void
  setIsLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void
  setHasHydrated: (hasHydrated: boolean) => void
  logout: () => void

  // Computed helpers
  isAuthenticated: () => boolean
  canUploadVideos: () => boolean
  isProvider: () => boolean
  isOrgAdmin: () => boolean
  isPlatformAdmin: () => boolean
  getRole: () => UserRole | null
  getTier: () => SubscriptionTier | null
}

// Tiers that can upload videos
const UPLOAD_TIERS: SubscriptionTier[] = ['advanced', 'corporate', 'platinum']

// Roles that have provider capabilities
const PROVIDER_ROLES: UserRole[] = ['provider', 'org_admin', 'platform_admin']

// Persisted store with profile caching to prevent flash of incorrect content
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      isLoading: true,
      isInitialized: false,
      _hasHydrated: false,

      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
      setProfile: (profile) => set({ profile }),
      setLoading: (isLoading) => set({ isLoading }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setInitialized: (isInitialized) => set({ isInitialized }),
      setHasHydrated: (_hasHydrated) => set({ _hasHydrated }),

      logout: () => {
        // Clear persisted storage to prevent stale data
        useAuthStore.persist.clearStorage()
        set({
          user: null,
          session: null,
          profile: null,
          isLoading: false,
          isInitialized: true,
        })
      },

      isAuthenticated: () => {
        const { user, session } = get()
        return !!user && !!session
      },

      canUploadVideos: () => {
        const { profile } = get()
        if (!profile) return false

        // Platform admins can always upload
        if (profile.role === 'platform_admin') return true

        // Must have provider role AND advanced+ tier
        const hasProviderRole = PROVIDER_ROLES.includes(profile.role)
        const hasUploadTier = UPLOAD_TIERS.includes(profile.subscription_tier)

        return hasProviderRole && hasUploadTier
      },

      isProvider: () => {
        const { profile } = get()
        if (!profile) return false
        return PROVIDER_ROLES.includes(profile.role)
      },

      isOrgAdmin: () => {
        const { profile } = get()
        return profile?.role === 'org_admin' || profile?.role === 'platform_admin'
      },

      isPlatformAdmin: () => {
        const { profile } = get()
        return profile?.role === 'platform_admin'
      },

      getRole: () => {
        const { profile } = get()
        return profile?.role ?? null
      },

      getTier: () => {
        const { profile } = get()
        return profile?.subscription_tier ?? null
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist profile data - session/user should be re-fetched from Supabase
      partialize: (state) => ({ profile: state.profile }),
      // Track when hydration completes
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
